import type { TBodyProps, TdProps, THeadProps, TableProps, ThProps, TrProps } from './Table.types'

/**
 * table — static tabular-markup primitive family.
 *
 * Frame, head, body, row, header cell, and data cell compose top-down into
 * one well-formed HTML table. Children render verbatim, in order, with no
 * transformation and no behavior (no sorting, selection, pagination). The
 * frame contains horizontal overflow so a wide table never forces the page
 * body to scroll sideways.
 *
 * THE COLUMNS ARE FIXED, NOT MEASURED. `table-fixed` means a column's width
 * comes from the caller's own column sizing (a <colgroup> passed as the first
 * child) and never from the content that happens to be in it. With automatic
 * layout, opening a collapsed group re-measured every column and the whole
 * grid jumped sideways under the reader's eye — the columns moved because the
 * ROWS changed, which is exactly the thing a table must never do. Cells clip
 * and ellipsize instead of pushing their neighbours around.
 */

export function Table({ children }: TableProps) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-surface">
      <table className="w-full table-fixed text-left text-sm">{children}</table>
    </div>
  )
}

export function THead({ children }: THeadProps) {
  return <thead className="border-b border-border bg-surface-muted">{children}</thead>
}

export function TBody({ children }: TBodyProps) {
  return <tbody className="divide-y divide-border">{children}</tbody>
}

/**
 * A row, in one of two readings. A DATA row is plain and answers the pointer
 * only when it is interactive. A GROUP row is the lid over a set of data rows —
 * it wears the theme's collapsible tint, so "this opens" is answered by the
 * surface itself rather than by a chevron the reader has to find.
 *
 * ⛔ THE TINT WAS BRIEFLY REPLACED BY AIR AND A RULE — no fill, a gap above
 * each lid, a strong hairline across it. The body already divides every row, so
 * a second heavier rule per group turned the table into a stack of rules.
 *
 * ⚠️ The hover fill lives HERE, per row, not as a blanket rule on the body: the
 * two readings hover differently, and a rule on the body outranked the row's
 * own fill and turned every group lid grey under the pointer.
 */
export function Tr({ children, onClick, groupHeader = false, nestedGroup = false, rowRef }: TrProps) {
  const interactive = onClick !== undefined
  return (
    <tr
      ref={rowRef}
      tabIndex={rowRef ? -1 : undefined}
      onClick={onClick}
      className={`transition-colors ${interactive ? 'cursor-pointer' : ''} ${
        groupHeader
          ? nestedGroup
            ? `bg-surface-sunken/50 ${interactive ? 'hover:bg-surface-sunken-hover/50' : ''}`
            : `bg-surface-sunken ${interactive ? 'hover:bg-surface-sunken-hover' : ''}`
          : interactive
            ? 'hover:bg-surface-muted'
            : ''
      }`}
    >
      {children}
    </tr>
  )
}

export function Th({ children, dragState = 'idle', ...rest }: ThProps) {
  return (
    <th
      {...rest}
      className={`group/column relative overflow-hidden px-4 py-3 text-xs font-semibold tracking-wide text-ellipsis whitespace-nowrap text-text-muted uppercase ${dragState === 'source' ? 'opacity-50' : dragState === 'target' ? 'bg-accent-muted ring-2 ring-inset ring-accent' : ''}`}
    >
      {children}
    </th>
  )
}

/** Indent only the first cell by its folder depth; preserve column alignment. */
export function Td({ children, indent = 0, ...rest }: TdProps) {
  return (
    <td
      {...rest}
      className="overflow-hidden py-3 pr-4 text-ellipsis whitespace-nowrap text-text"
      style={{ paddingLeft: `${1 + Math.max(0, indent) * 1.5}rem` }}
    >
      {children}
    </td>
  )
}
